import { useEffect } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Lenis from 'lenis';

gsap.registerPlugin(ScrollTrigger);

// Curvas fortes (Emil Kowalski): ease-out para entradas, ease-in-out para movimento em tela.
export const EASE_OUT = 'expo.out';
export const EASE_IN_OUT = 'power4.inOut';

export const prefersReduced = () =>
  typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

export const finePointer = () =>
  typeof window !== 'undefined' && window.matchMedia('(hover: hover) and (pointer: fine)').matches;

// Instância única do scroll suave, acessível para âncoras e para travar durante a abertura.
export const smooth = { lenis: null };

/**
 * Scroll suave (Lenis) sincronizado com o ticker do GSAP, para o ScrollTrigger
 * ler exatamente a mesma posição que a tela mostra. Desligado com reduced-motion.
 */
export function useLenis() {
  useEffect(() => {
    if (prefersReduced()) return undefined;
    const lenis = new Lenis({ lerp: 0.11, wheelMultiplier: 1, smoothWheel: true });
    smooth.lenis = lenis;
    // A abertura da Home pode ter começado antes do Lenis existir: respeita a trava.
    if (document.documentElement.classList.contains('hv3-intro-on')) lenis.stop();
    lenis.on('scroll', ScrollTrigger.update);
    const tick = (time) => lenis.raf(time * 1000);
    gsap.ticker.add(tick);
    gsap.ticker.lagSmoothing(0);
    return () => {
      gsap.ticker.remove(tick);
      gsap.ticker.lagSmoothing(500, 33);
      lenis.destroy();
      smooth.lenis = null;
    };
  }, []);
}

export function scrollToEl(target) {
  const el = typeof target === 'string' ? document.querySelector(target) : target;
  if (!el) return;
  if (smooth.lenis) smooth.lenis.scrollTo(el, { duration: 1.4, easing: (t) => 1 - Math.pow(1 - t, 4) });
  else el.scrollIntoView({ behavior: prefersReduced() ? 'auto' : 'smooth', block: 'start' });
}

/**
 * Botão magnético: o elemento é puxado na direção do cursor e o conteúdo
 * interno se desloca um pouco mais, dando profundidade. Tudo via quickTo
 * (fora do ciclo de render do React), só em ponteiro fino.
 */
export function useMagnetic(ref, { strength = 0.32, inner = 0.18 } = {}) {
  useEffect(() => {
    const el = ref.current;
    if (!el || !finePointer() || prefersReduced()) return undefined;
    const label = el.querySelector('[data-magnetic-inner]');
    const xTo = gsap.quickTo(el, 'x', { duration: 0.6, ease: 'elastic.out(1, 0.45)' });
    const yTo = gsap.quickTo(el, 'y', { duration: 0.6, ease: 'elastic.out(1, 0.45)' });
    const ixTo = label && gsap.quickTo(label, 'x', { duration: 0.6, ease: 'elastic.out(1, 0.45)' });
    const iyTo = label && gsap.quickTo(label, 'y', { duration: 0.6, ease: 'elastic.out(1, 0.45)' });

    const move = (e) => {
      const r = el.getBoundingClientRect();
      const dx = e.clientX - (r.left + r.width / 2);
      const dy = e.clientY - (r.top + r.height / 2);
      xTo(dx * strength);
      yTo(dy * strength);
      if (label) {
        ixTo(dx * inner);
        iyTo(dy * inner);
      }
    };
    const leave = () => {
      xTo(0);
      yTo(0);
      if (label) {
        ixTo(0);
        iyTo(0);
      }
    };
    el.addEventListener('pointermove', move);
    el.addEventListener('pointerleave', leave);
    return () => {
      el.removeEventListener('pointermove', move);
      el.removeEventListener('pointerleave', leave);
      gsap.killTweensOf([el, label].filter(Boolean));
    };
  }, [ref, strength, inner]);
}
